import React, { useCallback, useEffect, useState } from "react";
import "./Diagram.css";
import GanttTable from "./GanttTable";
import Table from "../TableDiagram/Table";
import axios from "axios";
import Modal from 'react-modal';
import { buildHierarchy, computeOrderDates, DataUtils } from "./dataTransformer";
import { mockOrderData } from "./mockData";
import InstructionModal from "./InstructionModal";

Modal.setAppElement('#root');

function Diagram() {
    const [searchTerm, setSearchTerm] = useState("");
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [operationModalOpen, setOperationModalOpen] = useState(false);
    const [operationData, setOperationData] = useState(null);
    const [dataSource, setDataSource] = useState('mock');
    const [notification, setNotification] = useState('');
    const [activeView, setActiveView] = useState('gantt');
    const [instructionModalOpen, setInstructionModalOpen] = useState(false);

    // Создание mock данных
    const createMockData = () => {
        return mockOrderData.map(order => ({
            ...order,
            id: order.orderId,
            startDate: order.startDate || new Date().toISOString(),
            endDate: order.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            operations: order.operations.map(op => ({
                ...op,
                id: op.operationId,
                resources: op.resources.map(res => ({
                    ...res,
                    id: res.agentId,
                    startDate: res.startDate || op.startDate,
                    endDate: res.endDate || op.endDate
                }))
            }))
        }));
    };

    // Загрузка данных из API
    const fetchApiData = async () => {
        try {
            console.log('Загрузка данных из API...');

            const [ordersResponse, operationsResponse, resourcesResponse] = await Promise.all([
                axios.get('https://monitor.dev.masiot.dmsolution.ru/api/v1/orders/'),
                axios.get('https://monitor.dev.masiot.dmsolution.ru/api/v1/operations/'),
                axios.get('https://monitor.dev.masiot.dmsolution.ru/api/v1/resources/')
            ]);

            let hierarchyData = buildHierarchy(
                ordersResponse.data || [],
                operationsResponse.data || [],
                resourcesResponse.data || []
            );

            hierarchyData = computeOrderDates(hierarchyData);
            return hierarchyData;

        } catch (error) {
            console.error('Ошибка загрузки данных из API:', error);
            throw error;
        }
    };

    // Загрузка mock данных
    const fetchMockData = async () => {
        const mockData = createMockData();
        const computedData = computeOrderDates(mockData);
        return computedData;
    };

    // Показ уведомления
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(''), 3000);
    };

    // Основная функция загрузки данных
    const fetchData = useCallback(async (source = dataSource) => {
        try {
            setLoading(true);
            let data;

            if (source === 'api') {
                data = await fetchApiData();
                showNotification('Данные загружены из API');
            } else {
                data = await fetchMockData();
                showNotification('Загружены демо-данные');
            }

            setOrders(data);
            setFilteredOrders(data);
            setDataSource(source);

        } catch (err) {
            console.error("Ошибка загрузки данных:", err);
            const mockData = await fetchMockData();
            setOrders(mockData);
            setFilteredOrders(mockData);
            setDataSource('mock');
            showNotification('Ошибка загрузки API. Используются демо-данные', 'error');
        } finally {
            setLoading(false);
        }
    }, [dataSource]);

    // Переключение между источниками данных
    const toggleDataSource = async () => {
        const newSource = dataSource === 'mock' ? 'api' : 'mock';
        await fetchData(newSource);
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Фильтрация данных при изменении поискового запроса
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredOrders(orders);
        } else {
            const filtered = DataUtils.searchByName(orders, searchTerm);
            setFilteredOrders(filtered);
        }
    }, [searchTerm, orders]);

    // Обработчик клика по элементу
    const handleItemClick = (item) => {
        console.log("Клик по элементу:", item);
        setOperationData(item);
        setOperationModalOpen(true);
    };

    // Обработчик изменения источника данных
    const handleDataSourceChange = async (source) => {
        await fetchData(source);
    };

    const getStatusColor = (status) => {
        const statusMap = {
            1: '#10b981',
            2: '#6b7280',
            3: '#f59e0b',
            4: '#ef4444',
            5: '#eab308',
            6: '#3b82f6',
            7: '#8b5cf6',
            10: '#10b981'
        };
        return statusMap[status] || '#6b7280';
    };

    const getStatusText = (status) => {
        const statusMap = {
            1: 'Утверждено',
            2: 'Завершено',
            3: 'Забронировано',
            4: 'Отменено',
            5: 'Готов к выполнению',
            6: 'В работе',
            7: 'На рассмотрении',
            10: 'Утверждено'
        };
        return statusMap[status] || 'Неизвестно';
    };

    // Обработчик переключения вида
    const handleViewChange = (view) => {
        setActiveView(view);
    };

    return (
        <div className="ds-container">
            {/* Уведомление */}
            {notification && (
                <div className={`ds-notification ds-notification--${notification.type}`}>
                    {notification.message}
                </div>
            )}

            <header className="ds-header">
                <div className="ds-header-info">
                    <div className="ds-view-toggle">
                        <button
                            className={`ds-view-btn ${activeView === 'gantt' ? 'ds-view-btn--active' : ''}`}
                            onClick={() => handleViewChange('gantt')}
                        >
                            <span className="ds-view-icon">📊</span>
                            Диаграмма Ганта
                        </button>
                        <button
                            className={`ds-view-btn ${activeView === 'table' ? 'ds-view-btn--active' : ''}`}
                            onClick={() => handleViewChange('table')}
                        >
                            <span className="ds-view-icon">📋</span>
                            Таблица
                        </button>
                    </div>
                    <p className="ds-subtitle">
                        {activeView === 'gantt'
                            ? 'Визуализация производственных процессов'
                            : 'Табличное представление заказов, операций и ресурсов'}
                    </p>
                </div>

                <div className="ds-header-controls">
                    {/* Кнопка помощи */}
                    <button
                        className="ds-help-btn"
                        onClick={() => setInstructionModalOpen(true)}
                        title="Открыть инструкцию"
                    >
                        <span className="ds-help-icon">?</span>
                        <span className="ds-help-text">Помощь</span>
                    </button>

                    <div className="ds-search">
                        <input
                            type="text"
                            className="ds-search-input"
                            placeholder={
                                activeView === 'gantt'
                                    ? "Поиск заказов, операций, ресурсов..."
                                    : "Поиск по названию, номеру, описанию..."
                            }
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="ds-data-controls">
                        {/* Переключатель данных */}
                        <div className="ds-data-toggle">
                            <span className="ds-toggle-label">Данные:</span>
                            <button
                                className={`ds-toggle-btn ${dataSource === 'mock' ? 'ds-toggle-btn--active' : ''}`}
                                onClick={() => handleDataSourceChange('mock')}
                                disabled={loading}
                            >
                                <span className="ds-toggle-icon">📊</span>
                                Демо
                            </button>
                            <button
                                className={`ds-toggle-btn ${dataSource === 'api' ? 'ds-toggle-btn--active' : ''}`}
                                onClick={() => handleDataSourceChange('api')}
                                disabled={loading}
                            >
                                <span className="ds-toggle-icon">🔄</span>
                                API
                            </button>
                        </div>

                        {/* Кнопка обновления */}
                        <button
                            className="ds-refresh-btn"
                            onClick={() => fetchData(dataSource)}
                            disabled={loading}
                            title="Обновить данные"
                        >
                            {loading ? (
                                <div className="ds-refresh-spinner"></div>
                            ) : (
                                <span className="ds-refresh-icon">↻</span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            <main className="ds-content">
                {loading ? (
                    <div className="ds-loading">
                        <div className="ds-loading-spinner"></div>
                        <p className="ds-loading-text">
                            {dataSource === 'api'
                                ? 'Загрузка данных из API...'
                                : 'Загрузка демо данных...'
                            }
                        </p>
                    </div>
                ) : (
                    <>
                        {filteredOrders.length === 0 ? (
                            <div className="ds-empty">
                                <div className="ds-empty-icon">
                                    {activeView === 'gantt' ? '🔍' : '📊'}
                                </div>
                                <h3 className="ds-empty-title">Ничего не найдено</h3>
                                <p className="ds-empty-text">Попробуйте изменить поисковый запрос</p>
                                <button
                                    className="ds-empty-reset"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Сбросить поиск
                                </button>
                            </div>
                        ) : (
                            <>
                                {activeView === 'gantt' ? (
                                    <GanttTable
                                        data={filteredOrders}
                                        onItemClick={handleItemClick}
                                        onDataSourceChange={handleDataSourceChange}
                                        currentDataSource={dataSource}
                                    />
                                ) : (
                                    <Table data={filteredOrders} />
                                )}
                            </>
                        )}
                    </>
                )}
            </main>

            {/* Модальное окно с инструкцией */}
            <InstructionModal
                isOpen={instructionModalOpen}
                onClose={() => setInstructionModalOpen(false)}
            />

            {/* Модальное окно с детальной информацией */}
            <Modal
                isOpen={operationModalOpen}
                onRequestClose={() => setOperationModalOpen(false)}
                className="ds-modal"
                overlayClassName="ds-modal-overlay"
            >
                {operationData && (
                    <div className="ds-modal-content">
                        <div className="ds-modal-header">
                            <div className="ds-modal-title-wrapper">
                                <div className={`ds-type-indicator ${
                                    operationData.orderNumber ? 'ds-type-order' :
                                        operationData.operationNumber ? 'ds-type-operation' :
                                            'ds-type-resource'
                                }`}>
                                    {operationData.orderNumber ? '📋' :
                                        operationData.operationNumber ? '⚙️' : '👤'}
                                </div>
                                <h2 className="ds-modal-title">Детальная информация</h2>
                            </div>
                            <button
                                className="ds-modal-close"
                                onClick={() => setOperationModalOpen(false)}
                                aria-label="Закрыть"
                            >
                                ×
                            </button>
                        </div>
                        <div className="ds-modal-body">
                            <div className="ds-info-grid">
                                <div className="ds-info-row">
                                    <label className="ds-info-label">Тип:</label>
                                    <span className="ds-info-value">
                                        {operationData.orderNumber ? 'Заказ' :
                                            operationData.operationNumber ? 'Операция' : 'Ресурс'}
                                    </span>
                                </div>
                                <div className="ds-info-row">
                                    <label className="ds-info-label">Название:</label>
                                    <span className="ds-info-value">{operationData.name || operationData.orderNumber || operationData.operationNumber}</span>
                                </div>
                                <div className="ds-info-row">
                                    <label className="ds-info-label">ID:</label>
                                    <span className="ds-info-value">{operationData.id}</span>
                                </div>
                                {operationData.status && (
                                    <div className="ds-info-row">
                                        <label className="ds-info-label">Статус:</label>
                                        <span className="ds-info-value">
                                            <span
                                                className="ds-status-indicator"
                                                style={{backgroundColor: getStatusColor(operationData.status)}}
                                            ></span>
                                            {getStatusText(operationData.status)}
                                        </span>
                                    </div>
                                )}
                                {operationData.startDate && (
                                    <div className="ds-info-row">
                                        <label className="ds-info-label">Начало:</label>
                                        <span className="ds-info-value">{new Date(operationData.startDate).toLocaleString('ru-RU')}</span>
                                    </div>
                                )}
                                {operationData.endDate && (
                                    <div className="ds-info-row">
                                        <label className="ds-info-label">Окончание:</label>
                                        <span className="ds-info-value">{new Date(operationData.endDate).toLocaleString('ru-RU')}</span>
                                    </div>
                                )}
                                {operationData.description && (
                                    <div className="ds-info-row ds-info-row--full">
                                        <label className="ds-info-label">Описание:</label>
                                        <span className="ds-info-value ds-info-value--block">{operationData.description}</span>
                                    </div>
                                )}
                                {operationData.type && (
                                    <div className="ds-info-row">
                                        <label className="ds-info-label">Тип ресурса:</label>
                                        <span className="ds-info-value">{operationData.type}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default Diagram;